import { AppView } from '../../types';

// ==========================================
// AI PLATFORM, LLMOPS & CONTROL PLANE TYPES
// ==========================================

export type AIProviderId = 'google-gemini' | 'openai' | 'anthropic' | 'self-hosted-vllm';

export interface AIProvider {
  id: AIProviderId;
  name: string;
  status: 'HEALTHY' | 'DEGRADED' | 'OUTAGE';
  capabilities: string[];
  dataResidencyRegions: string[];
  enterpriseDataPolicy: string;
  p95LatencyMs: number;
  errorRatePct: number;
}

export type AIModelStatus =
  | 'DISCOVERED'
  | 'ASSESSING'
  | 'APPROVED'
  | 'CANARY'
  | 'PRODUCTION'
  | 'DEPRECATED'
  | 'BLOCKED'
  | 'RETIRED';

export interface AIModelDefinition {
  id: string;
  provider: AIProviderId;
  providerModelId: string;
  internalKey: string; // e.g. 'general.quality', 'general.fast', 'financial.explanation'
  displayName: string;
  version: string;
  releaseChannel: 'STABLE' | 'PREVIEW' | 'EXPERIMENTAL';
  status: AIModelStatus;
  contextWindowTokens: number;
  maxOutputTokens: number;
  supportsStructuredOutputs: boolean;
  supportsToolCalling: boolean;
  supportsContextCaching: boolean;
  inputCostPer1kTokensUSD: number;
  outputCostPer1kTokensUSD: number;
  cachedInputCostPer1kTokensUSD: number;
  avgLatencyMs: number;
  financialAccuracyScore: number; // 0-100
  lastVerifiedAt: string;
}

export type AIRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface AITaskDefinition {
  id: string;
  taskKey: string;
  name: string;
  domain: string;
  riskLevel: AIRiskLevel;
  requiredCapabilities: string[];
  allowedModels: string[];
  fallbackPolicy: string;
  qualityTargetPct: number;
  latencyTargetMs: number;
  maxCostPerRequestUSD: number;
  dataEligibilityRequirement: string;
  owner: string;
}

export interface AIModelRoute {
  id: string;
  taskKey: string;
  primaryModelId: string;
  fallbackModelIds: string[];
  trafficPercentage: number;
  status: 'ACTIVE' | 'CANARY' | 'PAUSED' | 'TEST';
  reasonCode: 'PRIMARY' | 'COST_POLICY' | 'MODEL_UNHEALTHY' | 'CANARY_TEST';
  maxCostUSD: number;
  timeoutMs: number;
  maxRetries: number;
}

// ------------------------------------------
// PROMPT REGISTRY
// ------------------------------------------

export interface AIPromptVersion {
  version: string;
  templateText: string;
  systemPolicy: string;
  variables: string[];
  outputSchemaName?: string;
  approvedBy: string;
  approvedAt: string;
  evalPassRatePct: number;
  status: 'DRAFT' | 'EVALUATING' | 'APPROVED' | 'PRODUCTION' | 'DEPRECATED';
}

export interface AIPromptDefinition {
  id: string;
  name: string;
  taskKey: string;
  currentProductionVersion: string;
  versions: AIPromptVersion[];
  owner: string;
  riskLevel: AIRiskLevel;
  lastUpdated: string;
}

// ------------------------------------------
// RAGOPS & VECTOR INFRASTRUCTURE
// ------------------------------------------

export interface RAGCorpus {
  id: string;
  name: string;
  description: string;
  domain: string;
  sourceAssets: string[];
  aiEligibilityApproved: boolean;
  documentCount: number;
  chunkCount: number;
  embeddingModel: string;
  vectorIndexName: string;
  freshnessStatus: 'FRESH' | 'INDEXING' | 'STALE';
  owner: string;
}

export interface VectorIndexDefinition {
  id: string;
  name: string;
  corpusId: string;
  backend: 'pgvector_postgresql' | 'dedicated_cluster';
  dimension: number;
  distanceMetric: 'COSINE' | 'INNER_PRODUCT' | 'L2';
  vectorCount: number;
  activeVersion: string;
  stalenessLagMinutes: number;
  status: 'ACTIVE' | 'BUILDING' | 'SHADOW' | 'STALE';
}

export interface RetrievalProfile {
  id: string;
  name: string;
  retrievalStrategy: 'HYBRID_LEXICAL_VECTOR' | 'PURE_VECTOR' | 'GRAPH_GROUNDED';
  topK: number;
  rerankerEnabled: boolean;
  rerankerModel?: string;
  aclFilterEnforced: boolean;
  citationLineageEnabled: boolean;
}

// ------------------------------------------
// BOUNDED AGENT RUNTIME & TOOLS
// ------------------------------------------

export interface AIToolDefinition {
  id: string;
  name: string;
  description: string;
  domain: string;
  riskTier: AIRiskLevel;
  sideEffect: 'NONE' | 'REVERSIBLE' | 'FINANCIAL' | 'IRREVERSIBLE';
  requiresConfirmation: boolean;
  serverAuthorizationRole: string;
  isDeterministicCalculator: boolean;
  owner: string;
  status: 'APPROVED' | 'REVIEW' | 'DISABLED';
}

export interface AIAgentDefinition {
  id: string;
  name: string;
  purpose: string;
  allowedTools: string[];
  modelRouteKey: string;
  promptId: string;
  maxSteps: number;
  maxToolCalls: number;
  maxCostUSD: number;
  timeoutSeconds: number;
  status: 'PRODUCTION' | 'SANDBOX' | 'CANARY' | 'PAUSED';
  owner: string;
}

// ------------------------------------------
// GUARDRAILS & SCHEMAS
// ------------------------------------------

export interface AIGuardrailPolicy {
  id: string;
  name: string;
  type: 'INPUT_INJECTION' | 'FINANCIAL_ADVICE_LIMIT' | 'PII_REDACTION' | 'OUTPUT_SCHEMA' | 'COST_BUDGET';
  action: 'DENY' | 'TRANSFORM' | 'REQUIRE_CONFIRMATION';
  description: string;
  enforcedAt: 'GATEWAY_INPUT' | 'POST_RETRIEVAL' | 'PRE_TOOL' | 'GATEWAY_OUTPUT';
  status: 'ENFORCED' | 'SHADOW' | 'DISABLED';
}

export interface AIOutputSchema {
  id: string;
  name: string;
  jsonSchema: string;
  taskKey: string;
  strictMode: boolean;
  owner: string;
}

// ------------------------------------------
// EVALUATION & RELEASE MANAGEMENT
// ------------------------------------------

export interface AIEvaluationSuite {
  id: string;
  name: string;
  taskKey: string;
  testCaseCount: number;
  scorers: ('GROUNDEDNESS' | 'CITATION_ACCURACY' | 'FINANCIAL_CORRECTNESS' | 'SAFETY' | 'LATENCY')[];
  passThresholdPct: number;
  lastRunPassRatePct: number;
  lastRunDate: string;
  status: 'PASS' | 'WARN' | 'FAIL';
}

export interface AIReleaseCandidate {
  id: string;
  releaseName: string;
  taskKey: string;
  modelRouteId: string;
  promptVersion: string;
  ragIndexVersion?: string;
  evalPassRatePct: number;
  status: 'APPROVED' | 'SHADOW' | 'CANARY' | 'PRODUCTION' | 'ROLLED_BACK';
  rollbackTargetVersion: string;
  releasedAt: string;
  releasedBy: string;
}

// ------------------------------------------
// FINOPS & KILL SWITCHES
// ------------------------------------------

export interface AIFinOpsMetric {
  taskKey: string;
  dailyRequests: number;
  dailyCostUSD: number;
  avgCostPerRequestUSD: number;
  cacheHitRatePct: number;
  monthlyBudgetUSD: number;
  budgetConsumedPct: number;
}

export interface AIKillSwitches {
  globalAIEnabled: boolean;
  financialAssistantEnabled: boolean;
  searchRagEnabled: boolean;
  supportCopilotEnabled: boolean;
  agenticToolsEnabled: boolean;
  secondaryProviderFallbackEnabled: boolean;
  activeKillSwitchCount: number;
}

// ==========================================
// MOCK DATA STORES
// ==========================================

export const MOCK_AI_PROVIDERS: AIProvider[] = [
  {
    id: 'google-gemini',
    name: 'Google Gemini (Primary Enterprise Engine)',
    status: 'HEALTHY',
    capabilities: ['TEXT_GENERATION', 'STRUCTURED_OUTPUT', 'TOOL_CALLING', 'SEARCH_GROUNDING', 'CONTEXT_CACHING', 'EMBEDDINGS'],
    dataResidencyRegions: ['europe-west1 (Belgium)', 'us-central1 (Iowa)'],
    enterpriseDataPolicy: 'Zero Data Retention for Training. Enterprise SLA 99.99%',
    p95LatencyMs: 420,
    errorRatePct: 0.02
  },
  {
    id: 'openai',
    name: 'OpenAI API (Secondary Qualified Fallback)',
    status: 'HEALTHY',
    capabilities: ['TEXT_GENERATION', 'STRUCTURED_OUTPUT', 'TOOL_CALLING', 'EMBEDDINGS'],
    dataResidencyRegions: ['us-east1 (Virginia)'],
    enterpriseDataPolicy: 'Zero Data Retention for Training. Enterprise SLA 99.9%',
    p95LatencyMs: 650,
    errorRatePct: 0.05
  },
  {
    id: 'anthropic',
    name: 'Anthropic Claude API (Specialized Reasoning)',
    status: 'HEALTHY',
    capabilities: ['TEXT_GENERATION', 'REASONING', 'TOOL_CALLING', 'PROMPT_CACHING'],
    dataResidencyRegions: ['us-east1 (Virginia)'],
    enterpriseDataPolicy: 'Zero Data Retention for Training',
    p95LatencyMs: 580,
    errorRatePct: 0.04
  }
];

export const MOCK_AI_MODELS: AIModelDefinition[] = [
  {
    id: 'mod-gemini-35-flash',
    provider: 'google-gemini',
    providerModelId: 'gemini-3.5-flash',
    internalKey: 'general.fast',
    displayName: 'Gemini 3.5 Flash',
    version: '2026-v1',
    releaseChannel: 'STABLE',
    status: 'PRODUCTION',
    contextWindowTokens: 1000000,
    maxOutputTokens: 8192,
    supportsStructuredOutputs: true,
    supportsToolCalling: true,
    supportsContextCaching: true,
    inputCostPer1kTokensUSD: 0.00015,
    outputCostPer1kTokensUSD: 0.0006,
    cachedInputCostPer1kTokensUSD: 0.0000375,
    avgLatencyMs: 280,
    financialAccuracyScore: 98.4,
    lastVerifiedAt: '2026-08-25 04:00:00'
  },
  {
    id: 'mod-gemini-35-pro',
    provider: 'google-gemini',
    providerModelId: 'gemini-3.5-pro',
    internalKey: 'general.quality',
    displayName: 'Gemini 3.5 Pro (Deep Reasoning)',
    version: '2026-v2',
    releaseChannel: 'STABLE',
    status: 'PRODUCTION',
    contextWindowTokens: 2000000,
    maxOutputTokens: 8192,
    supportsStructuredOutputs: true,
    supportsToolCalling: true,
    supportsContextCaching: true,
    inputCostPer1kTokensUSD: 0.00125,
    outputCostPer1kTokensUSD: 0.005,
    cachedInputCostPer1kTokensUSD: 0.0003125,
    avgLatencyMs: 720,
    financialAccuracyScore: 99.7,
    lastVerifiedAt: '2026-08-25 04:00:00'
  },
  {
    id: 'mod-gpt4o-mini',
    provider: 'openai',
    providerModelId: 'gpt-4o-mini',
    internalKey: 'general.fallback_fast',
    displayName: 'GPT-4o Mini',
    version: '2025-07',
    releaseChannel: 'STABLE',
    status: 'APPROVED',
    contextWindowTokens: 128000,
    maxOutputTokens: 4096,
    supportsStructuredOutputs: true,
    supportsToolCalling: true,
    supportsContextCaching: false,
    inputCostPer1kTokensUSD: 0.00015,
    outputCostPer1kTokensUSD: 0.0006,
    cachedInputCostPer1kTokensUSD: 0.00015,
    avgLatencyMs: 410,
    financialAccuracyScore: 96.8,
    lastVerifiedAt: '2026-08-20 00:00:00'
  }
];

export const MOCK_AI_TASKS: AITaskDefinition[] = [
  {
    id: 'task-rag-financial-search',
    taskKey: 'RAG_FINANCIAL_SEARCH',
    name: 'Recherche & Assistance Financière RAG',
    domain: 'search',
    riskLevel: 'HIGH',
    requiredCapabilities: ['TEXT_GENERATION', 'STRUCTURED_OUTPUT', 'SEARCH_GROUNDING'],
    allowedModels: ['mod-gemini-35-flash', 'mod-gemini-35-pro', 'mod-gpt4o-mini'],
    fallbackPolicy: 'Fallback to mod-gpt4o-mini on 3x rate-limit or 5xx',
    qualityTargetPct: 99.0,
    latencyTargetMs: 800,
    maxCostPerRequestUSD: 0.01,
    dataEligibilityRequirement: 'RAG_ONLY or PUBLIC_CONTEXT_ALLOWED',
    owner: 'Principal RAG Architect'
  },
  {
    id: 'task-calc-explainer',
    taskKey: 'CALCULATOR_EXPLAINER',
    name: 'Explication des Simulations Financières',
    domain: 'universal-calculators',
    riskLevel: 'MEDIUM',
    requiredCapabilities: ['TEXT_GENERATION', 'STRUCTURED_OUTPUT'],
    allowedModels: ['mod-gemini-35-flash'],
    fallbackPolicy: 'Static deterministic response summary',
    qualityTargetPct: 98.0,
    latencyTargetMs: 400,
    maxCostPerRequestUSD: 0.002,
    dataEligibilityRequirement: 'PUBLIC_CONTEXT_ALLOWED',
    owner: 'Lead Calculator Engineer'
  }
];

export const MOCK_MODEL_ROUTES: AIModelRoute[] = [
  {
    id: 'route-rag-search-p0',
    taskKey: 'RAG_FINANCIAL_SEARCH',
    primaryModelId: 'mod-gemini-35-flash',
    fallbackModelIds: ['mod-gemini-35-pro', 'mod-gpt4o-mini'],
    trafficPercentage: 100,
    status: 'ACTIVE',
    reasonCode: 'PRIMARY',
    maxCostUSD: 0.015,
    timeoutMs: 3000,
    maxRetries: 2
  }
];

export const MOCK_PROMPTS: AIPromptDefinition[] = [
  {
    id: 'prompt-financial-copilot',
    name: 'Financial Copilot RAG Grounded System Prompt',
    taskKey: 'RAG_FINANCIAL_SEARCH',
    currentProductionVersion: 'v2.4.0',
    owner: 'Senior Prompt Engineer',
    riskLevel: 'HIGH',
    lastUpdated: '2026-08-22',
    versions: [
      {
        version: 'v2.4.0',
        templateText: 'Tu es l\'assistant financier officiel YamooPay. Réponds en citant impérativement les sources du contexte RAG {{rag_context}}. N\'invente aucun taux ni barème fiscal.',
        systemPolicy: 'Strict financial compliance. No speculative advice. Must cite documents.',
        variables: ['rag_context', 'user_query', 'jurisdiction'],
        outputSchemaName: 'FinancialAssistantResponseSchema',
        approvedBy: 'Chief AI Officer',
        approvedAt: '2026-08-22 10:00:00',
        evalPassRatePct: 99.4,
        status: 'PRODUCTION'
      },
      {
        version: 'v2.3.0',
        templateText: 'Assistant YamooPay v2.3...',
        systemPolicy: 'Standard response policy',
        variables: ['rag_context', 'user_query'],
        approvedBy: 'AI Platform Lead',
        approvedAt: '2026-07-10 00:00:00',
        evalPassRatePct: 97.2,
        status: 'DEPRECATED'
      }
    ]
  }
];

export const MOCK_RAG_CORPORA: RAGCorpus[] = [
  {
    id: 'corpus-tax-regulatory',
    name: 'Corpus Réglementaire & Barèmes Fiscaux Mondiaux',
    description: 'Ensemble des lois de finances, tranches d\'impôts et règles de cotisations 2025/2026 certifiées.',
    domain: 'salary-payroll',
    sourceAssets: ['asset-tax-rulesets-master', 'official_gazette_feeds'],
    aiEligibilityApproved: true,
    documentCount: 420,
    chunkCount: 18400,
    embeddingModel: 'text-embedding-004 (768d)',
    vectorIndexName: 'idx_pgvector_tax_rules_v2',
    freshnessStatus: 'FRESH',
    owner: 'Principal RAG Architect'
  }
];

export const MOCK_VECTOR_INDEXES: VectorIndexDefinition[] = [
  {
    id: 'idx-tax-rules-v2',
    name: 'idx_pgvector_tax_rules_v2',
    corpusId: 'corpus-tax-regulatory',
    backend: 'pgvector_postgresql',
    dimension: 768,
    distanceMetric: 'COSINE',
    vectorCount: 18400,
    activeVersion: 'v2.1.0',
    stalenessLagMinutes: 12,
    status: 'ACTIVE'
  }
];

export const MOCK_AI_TOOLS: AIToolDefinition[] = [
  {
    id: 'tool-calc-salary-tax',
    name: 'calculate_net_salary_exact',
    description: 'Exécute le moteur déterministe de calcul du salaire net et des impôts par pays.',
    domain: 'salary-payroll',
    riskTier: 'LOW',
    sideEffect: 'NONE',
    requiresConfirmation: false,
    serverAuthorizationRole: 'public_user_session',
    isDeterministicCalculator: true,
    owner: 'Lead Calculator Engineer',
    status: 'APPROVED'
  },
  {
    id: 'tool-fetch-fx-rate',
    name: 'get_canonical_fx_rate',
    description: 'Récupère le taux interbancaire certifié en direct entre deux devises ISO.',
    domain: 'financial-market-data',
    riskTier: 'LOW',
    sideEffect: 'NONE',
    requiresConfirmation: false,
    serverAuthorizationRole: 'public_user_session',
    isDeterministicCalculator: true,
    owner: 'Principal Market Data Architect',
    status: 'APPROVED'
  }
];

export const MOCK_AI_AGENTS: AIAgentDefinition[] = [
  {
    id: 'agent-financial-advisor-copilot',
    name: 'YamooPay Financial Advisor Copilot',
    purpose: 'Guider les utilisateurs dans leurs simulations fiscales, de transfert et de crédit.',
    allowedTools: ['calculate_net_salary_exact', 'get_canonical_fx_rate'],
    modelRouteKey: 'RAG_FINANCIAL_SEARCH',
    promptId: 'prompt-financial-copilot',
    maxSteps: 5,
    maxToolCalls: 2,
    maxCostUSD: 0.02,
    timeoutSeconds: 15,
    status: 'PRODUCTION',
    owner: 'Principal Agent Systems Architect'
  }
];

export const MOCK_AI_EVALS: AIEvaluationSuite[] = [
  {
    id: 'eval-financial-correctness',
    name: 'Suite d\'Évaluation Exactitude Financière & RAG',
    taskKey: 'RAG_FINANCIAL_SEARCH',
    testCaseCount: 250,
    scorers: ['GROUNDEDNESS', 'CITATION_ACCURACY', 'FINANCIAL_CORRECTNESS', 'SAFETY'],
    passThresholdPct: 99.0,
    lastRunPassRatePct: 99.4,
    lastRunDate: '2026-08-24 23:00:00',
    status: 'PASS'
  }
];

export const MOCK_AI_FINOPS: AIFinOpsMetric[] = [
  {
    taskKey: 'RAG_FINANCIAL_SEARCH',
    dailyRequests: 42500,
    dailyCostUSD: 14.85,
    avgCostPerRequestUSD: 0.000349,
    cacheHitRatePct: 34.2,
    monthlyBudgetUSD: 800.0,
    budgetConsumedPct: 54.2
  },
  {
    taskKey: 'CALCULATOR_EXPLAINER',
    dailyRequests: 89000,
    dailyCostUSD: 18.20,
    avgCostPerRequestUSD: 0.000204,
    cacheHitRatePct: 48.6,
    monthlyBudgetUSD: 900.0,
    budgetConsumedPct: 60.1
  }
];

export const MOCK_KILL_SWITCHES: AIKillSwitches = {
  globalAIEnabled: true,
  financialAssistantEnabled: true,
  searchRagEnabled: true,
  supportCopilotEnabled: true,
  agenticToolsEnabled: true,
  secondaryProviderFallbackEnabled: true,
  activeKillSwitchCount: 0
};

// ==========================================
// STORE CLASS
// ==========================================

class AIPlatformStore {
  private providers: AIProvider[] = [...MOCK_AI_PROVIDERS];
  private models: AIModelDefinition[] = [...MOCK_AI_MODELS];
  private tasks: AITaskDefinition[] = [...MOCK_AI_TASKS];
  private routes: AIModelRoute[] = [...MOCK_MODEL_ROUTES];
  private prompts: AIPromptDefinition[] = [...MOCK_PROMPTS];
  private corpora: RAGCorpus[] = [...MOCK_RAG_CORPORA];
  private vectorIndexes: VectorIndexDefinition[] = [...MOCK_VECTOR_INDEXES];
  private tools: AIToolDefinition[] = [...MOCK_AI_TOOLS];
  private agents: AIAgentDefinition[] = [...MOCK_AI_AGENTS];
  private evals: AIEvaluationSuite[] = [...MOCK_AI_EVALS];
  private finops: AIFinOpsMetric[] = [...MOCK_AI_FINOPS];
  private killSwitches: AIKillSwitches = { ...MOCK_KILL_SWITCHES };

  // Getters
  getProviders() { return this.providers; }
  getModels() { return this.models; }
  getTasks() { return this.tasks; }
  getRoutes() { return this.routes; }
  getPrompts() { return this.prompts; }
  getCorpora() { return this.corpora; }
  getVectorIndexes() { return this.vectorIndexes; }
  getTools() { return this.tools; }
  getAgents() { return this.agents; }
  getEvals() { return this.evals; }
  getFinOps() { return this.finops; }
  getKillSwitches() { return this.killSwitches; }

  // Toggle Kill Switch
  toggleKillSwitch(key: keyof Omit<AIKillSwitches, 'activeKillSwitchCount'>) {
    this.killSwitches[key] = !this.killSwitches[key];
    let count = 0;
    if (!this.killSwitches.globalAIEnabled) count++;
    if (!this.killSwitches.financialAssistantEnabled) count++;
    if (!this.killSwitches.searchRagEnabled) count++;
    if (!this.killSwitches.supportCopilotEnabled) count++;
    if (!this.killSwitches.agenticToolsEnabled) count++;
    if (!this.killSwitches.secondaryProviderFallbackEnabled) count++;
    this.killSwitches.activeKillSwitchCount = count;
  }

  // Promote Prompt Version
  promotePromptVersion(promptId: string, version: string) {
    const p = this.prompts.find((item) => item.id === promptId);
    if (p) {
      p.currentProductionVersion = version;
      p.versions.forEach((v) => {
        if (v.version === version) v.status = 'PRODUCTION';
        else if (v.status === 'PRODUCTION') v.status = 'DEPRECATED';
      });
    }
  }

  // Simulate AI Gateway Request Execution
  simulateGatewayInference(taskKey: string, promptText: string, userParams: Record<string, any>) {
    const task = this.tasks.find((t) => t.taskKey === taskKey) || this.tasks[0];
    const route = this.routes.find((r) => r.taskKey === taskKey) || this.routes[0];
    const model = this.models.find((m) => m.id === route.primaryModelId) || this.models[0];

    return {
      success: true,
      taskKey,
      resolvedRoute: route.id,
      executedProvider: model.provider,
      executedModel: model.displayName,
      tokensUsed: { input: 320, output: 145, cached: 80 },
      latencyMs: Math.round(model.avgLatencyMs * (0.9 + Math.random() * 0.2)),
      costUSD: Number((model.inputCostPer1kTokensUSD * 0.32 + model.outputCostPer1kTokensUSD * 0.145).toFixed(6)),
      safetyGuardrailPassed: true,
      citedSources: ['Loi de Finances 2026 Art. 42', 'ECB Rate Bulletin 2026-08'],
      generatedOutput: `[Simulated AI Response via ${model.displayName}] Selon le barème fiscal 2026 pour la juridiction sélectionnée, le taux effectif s'établit avec exactitude.`
    };
  }
}

export const aiPlatformStore = new AIPlatformStore();
